import type { HttpError } from "src-next/shared/api/httpClient";
import { httpPost } from "src-next/shared/api/httpClient";
import { normalizeCpf } from "src-next/shared/utils/cpf";

import type {
  MessagingPatient,
  SendManyWhatsAppMessageInput,
  SendMessageResult,
  SendWhatsAppMessageInput,
} from "../types";
import { oids } from "@/configs/Settings";
import { extractUniqueNamedVariables } from "../../messageTemplates/validation/messageTemplateValidation";

/**
 * Contrato do POST unitário do FluxZap (espelha `src/services/notification/PostFluxzap.tsx`).
 */
export interface FluxzapUnitaryPayload {
  requester: { reference: string };
  template?: string;
  for: string[];
  parameters?: Record<string, string>;
  config: {
    nome_paciente?: boolean | string;
    nome_clinica?: boolean | string;
  };
}

/**
 * Contrato do POST em lote do FluxZap (espelha `FluxzapBatchPayload` no legado).
 */
export interface FluxzapBatchPayload {
  requester: { reference: string };
  for: string[];
  config: {
    nome_paciente?: boolean | string;
    nome_clinica?: boolean | string;
  };
}

export type ResolveRecipientForReferenceResult =
  | { ok: true; reference: string }
  | { ok: false; errorMessage: string };

export type BuildUniqueForReferencesResult =
  | { ok: true; references: string[] }
  | { ok: false; errorMessage: string };

/**
 * Resolve a referência FHIR do destinatário para o campo `for` do FluxZap.
 * - `forReference` explícito (ex.: RelatedPerson no vet) tem prioridade.
 * - Caso contrário, monta `Patient/{oids.cpf}-{cpf}` com validação de CPF.
 */
export function resolveRecipientForReference(
  patient: MessagingPatient,
): ResolveRecipientForReferenceResult {
  const explicitForReference = String(patient.forReference ?? "").trim();

  if (explicitForReference) {
    return { ok: true, reference: explicitForReference };
  }

  const rawCpf = patient.cpf;
  if (!rawCpf || !String(rawCpf).trim()) {
    return {
      ok: false,
      errorMessage:
        "CPF do paciente é obrigatório para envio (WhatsApp via FluxZap).",
    };
  }

  const cpfDigits = normalizeCpf(String(rawCpf));
  if (cpfDigits.length !== 11) {
    return {
      ok: false,
      errorMessage: "CPF do paciente inválido ou incompleto.",
    };
  }

  return {
    ok: true,
    reference: `Patient/${oids.cpf}-${cpfDigits}`,
  };
}

/**
 * Monta `for[]` sem duplicatas (comparação por referência trimada).
 */
function buildUniqueForReferences(
  patients: MessagingPatient[],
): BuildUniqueForReferencesResult {
  const seen = new Set<string>();
  const references: string[] = [];

  for (const patient of patients) {
    const resolved = resolveRecipientForReference(patient);
    if (!resolved.ok) {
      return resolved;
    }

    const reference = resolved.reference.trim();
    if (!reference) {
      return {
        ok: false,
        errorMessage:
          "Destinatário inválido ou ausente para envio (forReference).",
      };
    }

    if (seen.has(reference)) continue;
    seen.add(reference);
    references.push(reference);
  }

  return { ok: true, references };
}

/**
 * Base da API de notificação (mesma ideia do legado `endpoints.notification`).
 */
const NOTIFICATION_BASE_URL = import.meta.env.VITE_ENDPOINT_NOTIFICATION ?? "";

/**
 * Timeout alinhado ao serviço legado (`PostFluxzap`: 8000 ms).
 */
const FLUXZAP_REQUEST_TIMEOUT_MS = 8000;

/**
 * Fallback opcional para `requester` quando a UI ainda não injeta `requesterReference`
 * (ex.: protótipos do dashboard).
 */

const DEFAULT_FLUXZAP_PHONE_NUMBER_ID = import.meta.env.VITE_FLUXZAP_DEFAULT_PHONE_NUMBER_ID?.trim() || "379190331933594";

function resolveFluxzapTemplateConfig(
  input: Pick<SendWhatsAppMessageInput, "template">,
): FluxzapUnitaryPayload["config"] {
  const headerText = String(input.template?.header ?? "");
  const bodyText = String(input.template?.preview ?? "");
  const variables = extractUniqueNamedVariables(`${headerText}\n${bodyText}`);

  return {
    nome_paciente: variables.includes("nome_paciente"),
    nome_clinica: variables.includes("nome_clinica"),
  };
}

function buildFluxzapUrl(): string {
  const base = NOTIFICATION_BASE_URL.replace(/\/$/, "");
  return `${base}/fluxzap`;
}

function resolveFluxzapBusinessIdFallback(
  wabaId: string | null | undefined,
): string {
  const fromEndpoint = String(wabaId ?? "").trim();
  if (fromEndpoint) return fromEndpoint;
  return DEFAULT_FLUXZAP_PHONE_NUMBER_ID;
}

function resolveFluxzapPhoneNumberId(
  phoneNumberId: string | null | undefined,
): string | null {
  const fromEndpoint = String(phoneNumberId ?? "").trim();
  return fromEndpoint ? fromEndpoint : null;
}

function buildFluxzapSendUrl(
  fluxzapBaseUrl: string,
  businessId: string,
  templateName: string,
): string {
  return `${fluxzapBaseUrl}/${encodeURIComponent(businessId)}/${encodeURIComponent(templateName)}`;
}

function extractFluxzapErrorMessage(error: unknown): string {
  const httpError = error as HttpError;
  const data = httpError.data as { error?: { message?: string } } | undefined;
  const nested = data?.error?.message;
  if (typeof nested === "string" && nested.trim()) return nested.trim();
  if (typeof httpError.message === "string" && httpError.message.trim()) {
    return httpError.message;
  }
  return "Erro ao enviar mensagem. Tente novamente.";
}

function pickMessageIdFromResponse(data: unknown): string {
  if (data && typeof data === "object" && "id" in data) {
    const id = (data as { id: unknown }).id;
    if (id !== undefined && id !== null) return String(id);
  }
  if (typeof data === "string" && data.trim()) return data.trim();
  return `fluxzap-${Date.now()}`;
}

/**
 * Monta parâmetros do template (evolução: mapear variáveis do WhatsApp + questionário).
 */
function buildTemplateParameters(
  input: SendWhatsAppMessageInput,
): Record<string, string> {
  const parameters: Record<string, string> = {};
  if (input.questionnaire?.id) {
    parameters.questionnaire_id = input.questionnaire.id;
  }
  return parameters;
}

/**
 * messageSendService
 *
 * Integração HTTP com FluxZap.
 * - Unitário (`send`): um destinatário em `for[]`.
 * - Lote (`sendBatch`): vários destinatários em um único POST com `for[]` agregado.
 *
 * Identificador do canal no path:
 * - Preferencial: `phoneNumberId` (Meta phone-number-id) do Endpoint FHIR (rank 1).
 * - Fallback (compatibilidade): `wabaId` (WABA) quando `phoneNumberId` não estiver disponível.
 *
 * Destinatário (`for`):
 * - Sem `forReference` ou com `RelatedPerson/*` legado: exige CPF e envia para
 *   `Patient/{oids.cpf}-{cpf}` (paciente humano ou tutor vet).
 * - `forReference` explícita que não seja RelatedPerson: repassa no payload.
 */
async function send(
  input: SendWhatsAppMessageInput,
): Promise<SendMessageResult> {
  const requesterReference = String(input.requesterReference ?? "").trim();

  if (!requesterReference) {
    return {
      status: "error",
      errorMessage:
        "Referência do solicitante (PractitionerRole) não configurada para envio FluxZap.",
    };
  }

  const resolvedRecipient = resolveRecipientForReference(input.patient);
  if (!resolvedRecipient.ok) {
    return {
      status: "error",
      errorMessage: resolvedRecipient.errorMessage,
    };
  }

  const templateName = String(input.template?.name ?? "").trim();
  if (!templateName) {
    return {
      status: "error",
      errorMessage: "Nome do template inválido ou ausente.",
    };
  }

  const payload: FluxzapUnitaryPayload = {
    requester: {
      reference: requesterReference,
    },
    for: [resolvedRecipient.reference],
    config: resolveFluxzapTemplateConfig(input),
  };

  const phoneNumberIdForUrl = resolveFluxzapPhoneNumberId(input.phoneNumberId);
  const businessIdFallbackForUrl = resolveFluxzapBusinessIdFallback(
    input.wabaId,
  );
  const businessIdForUrl = phoneNumberIdForUrl ?? businessIdFallbackForUrl;

  const url = buildFluxzapUrl();
  if (!url.startsWith("http")) {
    return {
      status: "error",
      errorMessage: "URL de notificação inválida (VITE_ENDPOINT_NOTIFICATION).",
    };
  }

  const postUrl = buildFluxzapSendUrl(url, businessIdForUrl, templateName);

  try {
    const data = await httpPost<unknown, FluxzapUnitaryPayload>(
      postUrl,
      payload,
      {
        timeout: FLUXZAP_REQUEST_TIMEOUT_MS,
        withCredentials: false,
      },
    );

    return {
      status: "success",
      messageId: pickMessageIdFromResponse(data),
    };
  } catch (error: unknown) {
    return {
      status: "error",
      errorMessage: extractFluxzapErrorMessage(error),
    };
  }
}

/**
 * Envio em lote: um único POST com todas as referências em `for[]`.
 * Valida cada destinatário antes do POST (falha upfront se algum for inválido).
 */
async function sendBatch(
  input: SendManyWhatsAppMessageInput,
): Promise<SendMessageResult> {
  const requesterReference = String(input.requesterReference ?? "").trim();

  if (!requesterReference) {
    return {
      status: "error",
      errorMessage:
        "Referência do solicitante (PractitionerRole) não configurada para envio FluxZap.",
    };
  }

  if (input.patients.length === 0) {
    return {
      status: "error",
      errorMessage: "Nenhum destinatário selecionado.",
    };
  }

  const resolvedFor = buildUniqueForReferences(input.patients);
  if (!resolvedFor.ok) {
    return {
      status: "error",
      errorMessage: resolvedFor.errorMessage,
    };
  }

  const templateName = String(input.template?.name ?? "").trim();
  if (!templateName) {
    return {
      status: "error",
      errorMessage: "Nome do template inválido ou ausente.",
    };
  }

  const payload: FluxzapBatchPayload = {
    requester: {
      reference: requesterReference,
    },
    for: resolvedFor.references,
    config: resolveFluxzapTemplateConfig(input),
  };

  const phoneNumberIdForUrl = resolveFluxzapPhoneNumberId(input.phoneNumberId);
  const businessIdFallbackForUrl = resolveFluxzapBusinessIdFallback(
    input.wabaId,
  );
  const businessIdForUrl = phoneNumberIdForUrl ?? businessIdFallbackForUrl;

  const url = buildFluxzapUrl();
  if (!url.startsWith("http")) {
    return {
      status: "error",
      errorMessage: "URL de notificação inválida (VITE_ENDPOINT_NOTIFICATION).",
    };
  }

  const postUrl = buildFluxzapSendUrl(url, businessIdForUrl, templateName);

  try {
    const data = await httpPost<unknown, FluxzapBatchPayload>(
      postUrl,
      payload,
      {
        timeout: FLUXZAP_REQUEST_TIMEOUT_MS,
        withCredentials: false,
      },
    );

    return {
      status: "success",
      messageId: pickMessageIdFromResponse(data),
    };
  } catch (error: unknown) {
    return {
      status: "error",
      errorMessage: extractFluxzapErrorMessage(error),
    };
  }
}

export const messageSendService = {
  send,
  sendBatch,
};
